import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_THEME,
  isCustomPalette,
  isThemeId,
  paletteToCssVars,
  ThemeId,
  THEMES,
  Theme,
  CUSTOM_THEME_ENTRY,
  CustomPalette,
} from './themes';
import { useAppSelector } from '@/store';
import { settingsApi } from '@/services/settingsApi';

interface ThemeContextValue {
  /** Current theme id ('dark' | ... | 'custom'). */
  themeId: ThemeId;
  /** All available themes — presets plus a `custom` entry if one exists. */
  themes: readonly Theme[];
  /** The user's saved custom palette, or null if they haven't made one. */
  customPalette: CustomPalette | null;
  /** Switch to a new theme. Persists to localStorage; syncs to account when logged in. */
  setTheme(id: ThemeId): void;
  /**
   * Save a custom palette. The palette is stored to localStorage immediately,
   * and best-effort PUT'd to the server when the user is authenticated.
   * Calling this also switches the active theme to `'custom'`.
   */
  saveCustomPalette(palette: CustomPalette): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'cobstream:theme';
const CUSTOM_PALETTE_KEY = 'cobstream:customTheme';

function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemeId(raw) ? raw : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

function readStoredCustomPalette(): CustomPalette | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CUSTOM_PALETTE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isCustomPalette(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

interface Props {
  children: ReactNode;
  /** Optional initial theme override — useful for tests. */
  initialTheme?: ThemeId;
  /** Optional initial palette override — useful for tests. */
  initialCustomPalette?: CustomPalette;
  /**
   * Disable the lazy fetch of the user's saved palette + the best-effort PUT
   * on save. The default `false` is what the live app wants; tests that don't
   * mock settingsApi.get/.update should pass `true` to keep unrelated specs
   * from accidentally hitting the network when auth state is `authenticated`.
   */
  disableAccountSync?: boolean;
}

export function ThemeProvider({ children, initialTheme, initialCustomPalette, disableAccountSync = false }: Props): JSX.Element {
  const [themeId, setThemeIdState] = useState<ThemeId>(() => initialTheme ?? readStoredTheme());
  const [customPalette, setCustomPaletteState] = useState<CustomPalette | null>(
    () => initialCustomPalette ?? readStoredCustomPalette(),
  );

  // Pull the current auth state so we can lazy-load the server-side palette
  // when the user signs in, and push updates back up. Guests have no row to
  // read or write, so we no-op for them.
  const authStatus = useAppSelector((s) => s.auth.status);
  const isGuest = useAppSelector((s) => Boolean(s.auth.user?.guest));
  const isLoggedIn = authStatus === 'authenticated' && !isGuest;
  const loadedFromServerRef = useRef(false);

  // Apply theme attributes + variables to <html> whenever any of them change.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    root.setAttribute('data-theme', themeId);
    if (themeId === 'custom' && customPalette) {
      for (const [k, v] of Object.entries(paletteToCssVars(customPalette))) {
        root.style.setProperty(k, v);
      }
      // Custom palettes are user-chosen and can be any luminance; mark
      // color-scheme by the bg's lightness so the UA paints sensible controls.
      root.style.colorScheme = isPaletteLight(customPalette) ? 'light' : 'dark';
    } else {
      // Tear down any previously-applied custom variables so a preset
      // override block isn't shadowed by stale inline values.
      for (const k of CUSTOM_VAR_NAMES) root.style.removeProperty(k);
      root.style.colorScheme = themeId === 'light' ? 'light' : 'dark';
    }
  }, [themeId, customPalette]);

  // When the user signs in, fetch their saved palette and adopt it if our
  // local copy is empty or differs. We only do this once per session so
  // logging out and back in within the same tab won't override fresh edits.
  useEffect(() => {
    if (disableAccountSync) return;
    if (!isLoggedIn) {
      loadedFromServerRef.current = false;
      return;
    }
    if (loadedFromServerRef.current) return;
    loadedFromServerRef.current = true;
    void settingsApi.get()
      .then((res) => {
        const serverPalette = res.settings.customTheme;
        if (serverPalette && isCustomPalette(serverPalette)) {
          setCustomPaletteState(serverPalette);
          try { window.localStorage.setItem(CUSTOM_PALETTE_KEY, JSON.stringify(serverPalette)); } catch { /* storage may be unavailable */ }
        }
      })
      .catch(() => {
        // Network / auth flakes are not fatal — we just keep what's local.
      });
  }, [isLoggedIn, disableAccountSync]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeIdState(id);
    try { window.localStorage.setItem(STORAGE_KEY, id); } catch { /* storage may be unavailable */ }
  }, []);

  const saveCustomPalette = useCallback((palette: CustomPalette) => {
    setCustomPaletteState(palette);
    setThemeIdState('custom');
    try {
      window.localStorage.setItem(CUSTOM_PALETTE_KEY, JSON.stringify(palette));
      window.localStorage.setItem(STORAGE_KEY, 'custom');
    } catch { /* storage may be unavailable */ }
    if (isLoggedIn && !disableAccountSync) {
      // Best-effort persist. We don't surface failures because the local copy
      // is the authoritative one for the current tab anyway.
      void settingsApi.update({ customTheme: palette }).catch(() => {});
    }
  }, [isLoggedIn, disableAccountSync]);

  const themes = useMemo<readonly Theme[]>(() => {
    return customPalette ? [...THEMES, CUSTOM_THEME_ENTRY] : THEMES;
  }, [customPalette]);

  const value = useMemo<ThemeContextValue>(
    () => ({ themeId, themes, customPalette, setTheme, saveCustomPalette }),
    [themeId, themes, customPalette, setTheme, saveCustomPalette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme() must be used inside a <ThemeProvider>');
  }
  return ctx;
}

// Names of every CSS variable that `paletteToCssVars` writes. Kept here so the
// cleanup-on-switch loop knows exactly what to tear down.
const CUSTOM_VAR_NAMES = [
  '--bg',
  '--bg-2',
  '--surface',
  '--surface-2',
  '--text',
  '--text-muted',
  '--text-dim',
  '--accent',
  '--accent-hover',
  '--accent-soft',
  '--on-accent',
  '--danger',
  '--border',
  '--body-gradient',
  '--shadow-card',
];

function isPaletteLight(p: CustomPalette): boolean {
  // Mirror themes.isLight without re-importing to avoid a circular dep.
  let h = p.bg.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}
